import type { AssessmentResult, WordScore } from '../types.ts';
import { similarity, tokenize } from './text.ts';

const MATCH_MIN_SIM = 0.5;

/**
 * Dopasowuje transkrypt (to, co rozpoznała przeglądarka) do zdania wzorcowego.
 * Maksymalizuje sumę podobieństw dopasowanych słów (jak LCS z wagami), z zachowaniem kolejności.
 * Słowa wzorca bez pary = Omission, nadmiarowe słowa z transkryptu = Insertion.
 *
 * To tryb ZASTĘPCZY (gdy nie ma Azure): wiemy tylko, jakie słowa rozpoznał silnik, a nie jak
 * dokładnie zostały wypowiedziane — wynik jest łagodniejszy i mniej precyzyjny.
 */
export function alignTranscript(reference: string, transcript: string): AssessmentResult {
  const ref = tokenize(reference);
  const hyp = tokenize(transcript);
  const n = ref.length;
  const m = hyp.length;

  // dp[i][j] = najlepsza suma podobieństw dla ref[0..i) i hyp[0..j)
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  const sim: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from({ length: m }, (_, j) => similarity(ref[i] ?? '', hyp[j] ?? '')),
  );
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const s = sim[i - 1]?.[j - 1] ?? 0;
      const diag = s >= MATCH_MIN_SIM ? (dp[i - 1]?.[j - 1] ?? 0) + 1 + s : -Infinity;
      const up = dp[i - 1]?.[j] ?? 0;
      const left = dp[i]?.[j - 1] ?? 0;
      (dp[i] as number[])[j] = Math.max(diag, up, left);
    }
  }

  // odtworzenie ścieżki
  const refMatch: (number | null)[] = new Array<number | null>(n).fill(null); // indeks hyp
  const hypUsed: boolean[] = new Array<boolean>(m).fill(false);
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    const cur = dp[i]?.[j] ?? 0;
    const s = sim[i - 1]?.[j - 1] ?? 0;
    const diag = s >= MATCH_MIN_SIM ? (dp[i - 1]?.[j - 1] ?? 0) + 1 + s : -Infinity;
    if (diag === cur && s >= MATCH_MIN_SIM) {
      refMatch[i - 1] = j - 1;
      hypUsed[j - 1] = true;
      i--;
      j--;
    } else if ((dp[i - 1]?.[j] ?? 0) === cur) {
      i--;
    } else {
      j--;
    }
  }

  const words: WordScore[] = [];
  let spoken = 0;
  for (let r = 0; r < n; r++) {
    const refWord = ref[r] ?? '';
    const h = refMatch[r];
    if (h === null || h === undefined) {
      words.push({ word: refWord, accuracy: 0, error: 'Omission' });
      continue;
    }
    spoken++;
    const s = sim[r]?.[h] ?? 0;
    if (s >= 0.999) words.push({ word: refWord, accuracy: 100, error: 'None' });
    else words.push({ word: refWord, accuracy: Math.round(s * 75), error: 'Mispronunciation' });
  }
  for (let k = 0; k < m; k++) {
    if (!hypUsed[k]) words.push({ word: hyp[k] ?? '', accuracy: 0, error: 'Insertion' });
  }

  const scored = words.filter((w) => w.error !== 'Insertion');
  const accuracy = scored.length === 0 ? 0 : Math.round(scored.reduce((a, w) => a + w.accuracy, 0) / scored.length);
  const completeness = n === 0 ? 0 : Math.round((spoken / n) * 100);

  return { source: 'transcript', recognizedText: transcript, completeness, accuracy, words };
}
